<script>
	// @ts-nocheck
	import { page } from '$app/stores';
	import { goto } from '$app/navigation';
	import { userStore } from '#/stores/user.svelte.js';
	import { Search, Settings } from 'lucide-svelte';
	import { taskSearchStore } from '#/stores/taskSearch.svelte.js';
	import { normalizeSettingIds, getPTMenuAccess } from '$lib/ptMenu.js';

	const taskListPaths = ['/letter_list', '/summary_list', '/docs_detection', '/docs_list'];
	const showSearch = $derived(taskListPaths.some((p) => $page.url.pathname.startsWith(p)));

	const allMenus = [
		{ name: '피드백레터', href: '/letter_list' },
		{ name: '요약 & 질문', href: '/summary_list' },
		{ name: '서류검출', href: '/docs_detection' },
		{ name: '서류레포트', href: '/docs_list' }
	];

	const isPT = $derived($userStore?.type === 'PT');
	const isIndeepAI = $derived($userStore?.type === 'indeepai');
	const ptAllowedSettingIds = $derived(normalizeSettingIds($userStore?.settingIds));
	let ptMenuAccess = $state(allMenus.map(() => false));
	let ptMenuLoadId = 0;

	async function loadPTMenuTasks(settingIds) {
		const loadId = ++ptMenuLoadId;
		ptMenuAccess = allMenus.map(() => false);

		if (settingIds.length === 0) return;

		const access = await getPTMenuAccess(settingIds);
		if (loadId !== ptMenuLoadId) return;
		ptMenuAccess = access;
	}

	$effect(() => {
		if (isPT) {
			loadPTMenuTasks(ptAllowedSettingIds);
		} else {
			ptMenuLoadId += 1;
			ptMenuAccess = allMenus.map(() => false);
		}
	});

	const menus = $derived(
		!$userStore
			? []
			: isPT
				? allMenus.filter((_, idx) => ptMenuAccess[idx])
				: isIndeepAI
					? allMenus.filter((m) => m.href !== '/task_management')
					: allMenus
	);

	let selfEditOpen = $state(false);
	let newMemberAddOpen = $state(false);
	let usersListOpen = $state(false);

	let SelfEditForm = $state(null);
	let NewMemberAddForm = $state(null);
	let UsersListModal = $state(null);

	async function openSelfEdit() {
		if (!SelfEditForm) {
			SelfEditForm = (await import('#/components/auth/SelfEditForm.svelte')).default;
		}
		selfEditOpen = true;
	}

	async function openNewMemberAdd() {
		if (!NewMemberAddForm) {
			NewMemberAddForm = (await import('#/components/auth/NewMemberAddForm.svelte')).default;
		}
		newMemberAddOpen = true;
	}

	async function openUsersList() {
		if (!UsersListModal) {
			UsersListModal = (await import('#/components/auth/UsersListModal.svelte')).default;
		}
		usersListOpen = true;
	}
</script>

<header
	class="flex items-center w-full h-[7%] shrink-0 px-xxxl mb-m border-b border-neutral90 bg-neutral100"
>
	<!-- 로고 -->
	<button
		class="flex items-center shrink-0 mr-xxxl focus:outline-none focus:ring-0 cursor-pointer"
		aria-label="홈으로 이동"
	>
		<img src="/assets/findeet-logo.svg" alt="Findeet Logo" class="w-[90px]" />
	</button>

	<!-- 메뉴 -->
	<nav class="flex-1 h-full">
		<ul class="flex items-stretch h-full gap-xxl">
			{#each menus as menu}
				<li class="flex">
					<a
						href={menu.href}
						class="flex items-center gap-m px-l text-base-title-m transition-colors
						{$page.url.pathname.includes(menu.href)
							? 'text-primary01 border-b-2 border-primary01 -mb-px'
							: 'text-neutral50 hover:text-primary01 hover:border-b-2 hover:border-primary01 -mb-px'}
						focus:text-primary01 outline-none"
					>
						{menu.name}
					</a>
				</li>
			{/each}
		</ul>
	</nav>

	<!-- 검색 -->
	{#if showSearch}
		<div class="flex items-center gap-m px-l py-m mr-xxl border border-neutral80 rounded-s">
			<Search size={14} class="shrink-0 text-neutral50" />
			<input
				type="text"
				bind:value={$taskSearchStore}
				placeholder="과업명으로 검색"
				class="w-[180px] bg-transparent text-base-body-m text-neutral20 placeholder:text-neutral60 outline-none"
			/>
		</div>
	{/if}

	<!-- 유저 -->
	{#if $userStore}
		<div class="flex items-center gap-xxl">
			{#if !isPT}
				<div class="dropdown dropdown-end dropdown-bottom">
					<button
						tabindex="0"
						class="flex items-center cursor-pointer hover:opacity-80 focus:outline-none"
					>
						<Settings size={22} class="shrink-0 text-neutral50" />
					</button>

					<!-- svelte-ignore a11y_no_noninteractive_tabindex -->
					<ul
						tabindex="0"
						class="dropdown-content menu w-52 p-m mt-s rounded-s text-base-body-m bg-neutral100 shadow-2xl ring-2 ring-neutral0/7 z-[100]"
					>
						<li>
							<button class="w-full text-left" onclick={openNewMemberAdd}>
								신규 유저 등록하기
							</button>
						</li>
						<li>
							<button class="w-full text-left" onclick={openUsersList}> 모든 계정 확인하기 </button>
						</li>
					</ul>
				</div>
			{/if}

			<div class="dropdown dropdown-end dropdown-bottom">
				<button
					tabindex="0"
					class="flex items-center cursor-pointer hover:opacity-80 focus:outline-none"
				>
					<div
						class="flex items-center justify-center shrink-0 w-[32px] h-[32px] rounded-full text-base-title-m text-neutral100"
						style="background-color: {$userStore.color ?? '#34c759'}"
					>
						{$userStore.name[0] ?? '?'}
					</div>
				</button>

				<!-- svelte-ignore a11y_no_noninteractive_tabindex -->
				<ul
					tabindex="0"
					class="dropdown-content menu w-52 p-m mt-s rounded-s text-base-body-m bg-neutral100 shadow-2xl ring-2 ring-neutral0/7 z-[100]"
				>
					<li>
						<button class="w-full text-left" onclick={openSelfEdit}> 비밀번호 변경 </button>
					</li>
					<li><a href="/auth/logout">로그아웃</a></li>
				</ul>
			</div>
		</div>
	{/if}
</header>

{#if selfEditOpen && SelfEditForm}
	<div class="fixed inset-0 flex items-center justify-center bg-neutral0/50 z-[200]">
		<SelfEditForm variant="modal" onClose={() => (selfEditOpen = false)} />
	</div>
{/if}
{#if newMemberAddOpen && NewMemberAddForm}
	<div class="fixed inset-0 flex items-center justify-center bg-neutral0/50 z-[200]">
		<NewMemberAddForm variant="modal" onClose={() => (newMemberAddOpen = false)} />
	</div>
{/if}
{#if usersListOpen && UsersListModal}
	<div class="fixed inset-0 flex items-center justify-center bg-neutral0/50 z-[200]">
		<UsersListModal onClose={() => (usersListOpen = false)} />
	</div>
{/if}
